/**
 * Anything that relates to handling entitlement body payloads, including transforming
 * or absorbing them into state, should live here.
 */

import Ajv, { type JSONSchemaType } from 'ajv';

import { aPersonCanInteractWithPaywalls } from '@canvas/capability-summaries';
import { MetricName, ReliabilityMetrics } from '@canvas/frontend-metrics';
import { safeGet, safeSet } from '@canvas/idb-cache';
import {
  type FeatureBody,
  PaywallFeature,
  type ExpectedEntitlementFeatureBody,
  type EntitlementFeatureBody,
  type PaywallFeatureStateFromEntitlement,
} from '@canvas/paywall-types';
import {
  CustomerImpact,
  EventClassification,
  addBreadcrumb,
  captureMessageForCapability,
} from '@canvas/sentry-react';

import { getCTAConfigForFeature } from './paywall-cta-config';
import {
  currentPaywallFeatureState,
  RELEASED_PAYWALL_FEATURES,
  ALL_FEATURES,
} from './paywall-features-state';

const featureSchema: JSONSchemaType<FeatureBody> = {
  type: 'object',
  properties: {
    isEntitled: { type: 'boolean' },
  },
  required: ['isEntitled'],
  additionalProperties: true,
};

export const getEntitlementFeaturesValidator = (
  required: ReadonlyArray<PaywallFeature> = RELEASED_PAYWALL_FEATURES
) => {
  const expectedEntitlementFeatureSchema: JSONSchemaType<ExpectedEntitlementFeatureBody> =
    {
      type: 'object',
      properties: {
        whiteboards: {
          type: 'object',
          // Generates JSON schema properties object by iterating on PaywallFeature enum
          properties: Object.fromEntries(
            Object.values(PaywallFeature).map((paywallFeature) => {
              return [
                paywallFeature,
                featureSchema satisfies JSONSchemaType<FeatureBody>,
              ];
            })
            // Some ugly typecasting due to Object.fromEntries() widening the key type
            // to `string` rather than `PaywallFeature`
          ) as unknown as Record<PaywallFeature, JSONSchemaType<FeatureBody>>,
          required,
          additionalProperties: true,
        },
      },
      required: ['whiteboards'],
      additionalProperties: true,
    };

  const ajv = new Ajv({ allErrors: true });

  return ajv.compile(expectedEntitlementFeatureSchema);
};

const validateEntitlementFeatures = getEntitlementFeaturesValidator();

export const validateEntitlementAndTransformToPaywallState = (
  entitlementFeatures: EntitlementFeatureBody | undefined
): PaywallFeatureStateFromEntitlement | undefined => {
  if (!validateEntitlementFeatures(entitlementFeatures)) {
    const errorMessage =
      'Entitlement response is missing data that was expected to be present by the paywall feature schema';

    ReliabilityMetrics.getInstance().failure(
      MetricName.PAYWALL_FEATURE_SCHEMA_VALIDATION,
      { errorMessage }
    );

    captureMessageForCapability(
      errorMessage,
      {
        contexts: {
          'Entitlement response': entitlementFeatures,
          'Paywall feature schema validation errors': {
            ...validateEntitlementFeatures.errors,
          },
        },
        tags: {
          errorId: 'cta-paywall-entitlement-features',
          captureLocation: 'validateEntitlementAndTransformToPaywall-invalid-schema',
          customerImpact: CustomerImpact.CUSTOMERS,
          eventClassification: EventClassification.BUG,
        },
        level: 'error',
      },
      /**
       * Since the schema is corrupted, we might not see the paywall at all (at the time of writing we allow the feature when we can't find the right data)
       */
      aPersonCanInteractWithPaywalls
    );
  }

  return entitlementFeatures?.whiteboards;
};

/**
 * Makes sure the CTA image is shown instantly upon being trigerred.
 * Note that you should avoid trigerring unless you know for a fact that the user is not
 * allowed to use a given feature - Otherwise the image will never actually be seen.
 */
const preloadFeatureCtaImages = (feature: PaywallFeature) => {
  const ctaConfig = getCTAConfigForFeature(feature);
  if (!ctaConfig) {
    return;
  }
  const imageSrc = ctaConfig.image.src;

  new Image().src = imageSrc.dark;
  new Image().src = imageSrc.default;
};

export const updatePaywallFeatureState = (
  updatedState: PaywallFeatureStateFromEntitlement
) => {
  for (const uncastFeatureKey of Object.keys(updatedState)) {
    if (!ALL_FEATURES.includes(uncastFeatureKey as PaywallFeature)) {
      continue;
    }

    const featureKey = uncastFeatureKey as PaywallFeature;

    currentPaywallFeatureState[featureKey] = {
      ...currentPaywallFeatureState[featureKey],
      ...updatedState[featureKey],
    };

    if (!currentPaywallFeatureState[featureKey].isEntitled) {
      preloadFeatureCtaImages(featureKey);
    }
  }

  addBreadcrumb({
    type: 'info',
    level: 'info',
    message: 'Updated the current paywall feature state',
    data: currentPaywallFeatureState,
  });
};

export const WHITEBOARD_PAYWALL_IDB_CACHE_KEY = 'whiteboard-paywall-feature-data';

export const setPaywallCacheFeatureData = (
  data: PaywallFeatureStateFromEntitlement
): Promise<void> => {
  return safeSet(WHITEBOARD_PAYWALL_IDB_CACHE_KEY, data);
};

export const getPaywallCacheFeatureData = (): Promise<
  PaywallFeatureStateFromEntitlement | undefined
> => {
  return safeGet(WHITEBOARD_PAYWALL_IDB_CACHE_KEY);
};
